/**
 * Translation hook
 * Provides translation functionality for React components
 */

import React, { useContext, createContext } from 'react';
import type { MapLanguage } from '../types';
import { getTranslations } from '../locales';
import type { TranslationKeys } from '../utils/i18n';

/**
 * Translation context
 */
interface TranslationContextType {
  language: MapLanguage;
  t: TranslationKeys;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

/**
 * Translation provider props
 */
interface TranslationProviderProps {
  language: MapLanguage;
  children: React.ReactNode;
}

/**
 * Translation provider component
 */
export function TranslationProvider({ language, children }: TranslationProviderProps) {
  const translations = getTranslations(language);

  return (
    <TranslationContext.Provider value={{ language, t: translations }}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Hook to access translations
 * @returns Translation keys and current language
 */
export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}

